import React, { useEffect, useState } from 'react';
import { ProjectRow, CapitalProject, ProjectHealthReport } from '../../lib/types';
import { getProjects, getCapitalProjects } from '../api/client';
import { getApiUrl } from '../lib/api-config';
import styles from './portfolio-dashboard.module.css';

interface ProjectWithHealth extends ProjectRow {
  ragStatus?: 'Green' | 'Amber' | 'Red';
  budgetAmount?: number;
  priority?: 'P1' | 'P2' | 'P3' | 'P4';
}

interface FilterState {
  status: string;
  priority: string;
  domain: string;
  rag: string;
}

interface SortOption {
  key: string;
  label: string;
}

export default function PortfolioDashboard() {
  const [projects, setProjects] = useState<ProjectWithHealth[]>([]);
  const [capitals, setCapitals] = useState<CapitalProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    status: '',
    priority: '',
    domain: '',
    rag: '',
  });
  const [sortBy, setSortBy] = useState<string>('name');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

      const [projectsRes, capitalsRes, healthRes] = await Promise.all([
        getProjects(),
        getCapitalProjects(),
        fetch(getApiUrl('/health-reports/summary'), { headers: authHeaders }).then(r => r.json()),
      ]);

      if (projectsRes.success && projectsRes.data) {
        // Map health report data to projects
        const healthMap = new Map<string, string>();
        if (healthRes.success && healthRes.data) {
          healthRes.data.healthReports?.forEach((report: ProjectHealthReport) => {
            if (!healthMap.has(report.ProjectId) ||
                new Date(report.SubmittedDate) > new Date(healthMap.get(report.ProjectId) || '')) {
              healthMap.set(report.ProjectId, report.RAGStatus);
            }
          });
        }

        const projectsWithHealth = projectsRes.data.map((p: ProjectRow) => ({
          ...p,
          ragStatus: healthMap.get(p.ProjectId) || 'Green',
          priority: p.Priority || undefined,
        }));
        setProjects(projectsWithHealth);
      }

      if (capitalsRes.success && capitalsRes.data) {
        setCapitals(capitalsRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load portfolio data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary metrics
  const totalProjects = projects.length;
  const totalBudget = capitals.reduce((sum, c) => sum + (c.BudgetAmount || 0), 0);
  const onTimeCount = projects.filter(p =>
    ['ReadyForDelivery', 'InDelivery', 'Complete'].includes(p.Status)
  ).length;
  const atRiskCount = projects.filter(p => p.ragStatus === 'Red').length;

  // Calculate distributions
  const statusDistribution = projects.reduce((acc, p) => {
    acc[p.Status] = (acc[p.Status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const ragDistribution = {
    Green: projects.filter(p => p.ragStatus === 'Green').length,
    Amber: projects.filter(p => p.ragStatus === 'Amber').length,
    Red: projects.filter(p => p.ragStatus === 'Red').length,
  };

  const priorityDistribution = {
    P1: projects.filter(p => p.priority === 'P1').length,
    P2: projects.filter(p => p.priority === 'P2').length,
    P3: projects.filter(p => p.priority === 'P3').length,
    P4: projects.filter(p => p.priority === 'P4').length,
  };

  const domainDistribution = projects.reduce((acc, p) => {
    acc[p.Domain] = (acc[p.Domain] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Filter projects
  let filteredProjects = projects;
  if (filters.status) {
    filteredProjects = filteredProjects.filter(p => p.Status === filters.status);
  }
  if (filters.priority) {
    filteredProjects = filteredProjects.filter(p => p.priority === filters.priority);
  }
  if (filters.domain) {
    filteredProjects = filteredProjects.filter(p => p.Domain === filters.domain);
  }
  if (filters.rag) {
    filteredProjects = filteredProjects.filter(p => p.ragStatus === filters.rag);
  }

  // Sort projects
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    switch (sortBy) {
      case 'status':
        return a.Status.localeCompare(b.Status);
      case 'priority':
        return (a.priority || 'P4').localeCompare(b.priority || 'P4');
      case 'date':
        return new Date(b.CreatedDate).getTime() - new Date(a.CreatedDate).getTime();
      case 'name':
      default:
        return a.Title.localeCompare(b.Title);
    }
  });

  // Pie chart calculation
  const ragTotal = ragDistribution.Green + ragDistribution.Amber + ragDistribution.Red;

  // Priority bar chart calculation
  const priorityTotal = Object.values(priorityDistribution).reduce((a, b) => a + b, 0);

  const getStatusBadgeColor = (status: string): string => {
    const colors: Record<string, string> = {
      NewIdea: '#94a3b8',
      GatheringFeedback: '#3b82f6',
      ReadyForGovernance: '#06b6d4',
      UnderGovernanceReview: '#f59e0b',
      BelowTheLine: '#ef4444',
      AboveTheLine: '#10b981',
      InValidation: '#8b5cf6',
      InPlanning: '#ec4899',
      ReadyForDelivery: '#6366f1',
      InDelivery: '#0ea5e9',
      Complete: '#22c55e',
    };
    return colors[status] || '#64748b';
  };

  const getRagColor = (rag: string | undefined): string => {
    switch (rag) {
      case 'Green':
        return '#22c55e';
      case 'Amber':
        return '#f59e0b';
      case 'Red':
        return '#ef4444';
      default:
        return '#94a3b8';
    }
  };

  const getPriorityColor = (priority: string | undefined): string => {
    switch (priority) {
      case 'P1':
        return '#dc2626';
      case 'P2':
        return '#f59e0b';
      case 'P3':
        return '#3b82f6';
      case 'P4':
        return '#94a3b8';
      default:
        return '#64748b';
    }
  };

  if (loading) {
    return (
      <div className={styles.portfolioDashboard}>
        <h1 className={styles.pageTitle}>Portfolio Dashboard</h1>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          Loading portfolio data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.portfolioDashboard}>
        <h1 className={styles.pageTitle}>Portfolio Dashboard</h1>
        <div className={styles.errorBanner}>{error}</div>
      </div>
    );
  }

  return (
    <div className={styles.portfolioDashboard}>
      <h1 className={styles.pageTitle}>Portfolio Dashboard</h1>

      {/* Summary Cards */}
      <div className={styles.summaryCards}>
        <div className={styles.card}>
          <div className={styles.cardLabel}>Total Projects</div>
          <div className={styles.cardValue}>{totalProjects}</div>
          <div className={styles.cardSubtext}>across all domains</div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardLabel}>Total Budget</div>
          <div className={styles.cardValue}>${(totalBudget / 1000000).toFixed(1)}M</div>
          <div className={styles.cardSubtext}>capital projects</div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardLabel}>On-Time/Delivery</div>
          <div className={styles.cardValue} style={{ color: '#10b981' }}>
            {onTimeCount}
          </div>
          <div className={styles.cardSubtext}>
            {totalProjects > 0
              ? `${Math.round((onTimeCount / totalProjects) * 100)}%`
              : '0%'}
            of portfolio
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardLabel}>At Risk</div>
          <div className={styles.cardValue} style={{ color: '#ef4444' }}>
            {atRiskCount}
          </div>
          <div className={styles.cardSubtext}>
            {totalProjects > 0
              ? `${Math.round((atRiskCount / totalProjects) * 100)}%`
              : '0%'}
            RAG Red
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className={styles.chartsSection}>
        {/* Status Distribution Pie Chart */}
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Status Distribution</h3>
          <div className={styles.chartContainer}>
            {totalProjects > 0 ? (
              <svg
                viewBox="0 0 200 200"
                className={styles.pieChart}
                style={{ maxWidth: '200px', margin: '0 auto' }}
              >
                {Object.entries(statusDistribution)
                  .filter(([_, count]) => count > 0)
                  .reduce(
                    (acc, [status, count]) => {
                      const percentage = count / totalProjects;
                      const startAngle = acc.angle;
                      const endAngle = acc.angle + percentage * 360;
                      const radius = 60;
                      const startX =
                        100 +
                        radius * Math.cos(((startAngle - 90) * Math.PI) / 180);
                      const startY =
                        100 +
                        radius * Math.sin(((startAngle - 90) * Math.PI) / 180);
                      const endX =
                        100 +
                        radius * Math.cos(((endAngle - 90) * Math.PI) / 180);
                      const endY =
                        100 +
                        radius * Math.sin(((endAngle - 90) * Math.PI) / 180);
                      const largeArc = percentage > 0.5 ? 1 : 0;

                      const pathData = [
                        `M 100 100`,
                        `L ${startX} ${startY}`,
                        `A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY}`,
                        'Z',
                      ].join(' ');

                      return {
                        angle: endAngle,
                        paths: [
                          ...acc.paths,
                          (
                            <path
                              key={status}
                              d={pathData}
                              fill={getStatusBadgeColor(status)}
                              stroke="white"
                              strokeWidth="2"
                            />
                          ),
                        ],
                      };
                    },
                    { angle: 0, paths: [] as React.ReactElement[] }
                  ).paths}
              </svg>
            ) : (
              <div className={styles.emptyChart}>No data</div>
            )}
          </div>
          <div className={styles.chartLegend}>
            {Object.entries(statusDistribution)
              .filter(([_, count]) => count > 0)
              .map(([status, count]) => (
                <div key={status} className={styles.legendItem}>
                  <div
                    className={styles.legendColor}
                    style={{ backgroundColor: getStatusBadgeColor(status) }}
                  />
                  <span className={styles.legendLabel}>
                    {status}: {count}
                  </span>
                </div>
              ))}
          </div>
        </div>

        {/* RAG Status Bar Chart */}
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>RAG Status</h3>
          <div className={styles.chartContainer}>
            {ragTotal > 0 ? (
              <div className={styles.barChart}>
                <div className={styles.barRow}>
                  <span className={styles.barLabel}>Green</span>
                  <div className={styles.barContainer}>
                    <div
                      className={styles.bar}
                      style={{
                        width: `${(ragDistribution.Green / ragTotal) * 100}%`,
                        backgroundColor: '#22c55e',
                      }}
                    />
                  </div>
                  <span className={styles.barCount}>{ragDistribution.Green}</span>
                </div>

                <div className={styles.barRow}>
                  <span className={styles.barLabel}>Amber</span>
                  <div className={styles.barContainer}>
                    <div
                      className={styles.bar}
                      style={{
                        width: `${(ragDistribution.Amber / ragTotal) * 100}%`,
                        backgroundColor: '#f59e0b',
                      }}
                    />
                  </div>
                  <span className={styles.barCount}>{ragDistribution.Amber}</span>
                </div>

                <div className={styles.barRow}>
                  <span className={styles.barLabel}>Red</span>
                  <div className={styles.barContainer}>
                    <div
                      className={styles.bar}
                      style={{
                        width: `${(ragDistribution.Red / ragTotal) * 100}%`,
                        backgroundColor: '#ef4444',
                      }}
                    />
                  </div>
                  <span className={styles.barCount}>{ragDistribution.Red}</span>
                </div>
              </div>
            ) : (
              <div className={styles.emptyChart}>No data</div>
            )}
          </div>
        </div>

        {/* Priority Stacked Bar Chart */}
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Priority Distribution</h3>
          <div className={styles.chartContainer}>
            {priorityTotal > 0 ? (
              <div className={styles.stackedBar}>
                <div className={styles.stackedBarContainer}>
                  <div
                    className={styles.stackedSegment}
                    style={{
                      width: `${(priorityDistribution.P1 / priorityTotal) * 100}%`,
                      backgroundColor: '#dc2626',
                    }}
                    title={`P1: ${priorityDistribution.P1}`}
                  />
                  <div
                    className={styles.stackedSegment}
                    style={{
                      width: `${(priorityDistribution.P2 / priorityTotal) * 100}%`,
                      backgroundColor: '#f59e0b',
                    }}
                    title={`P2: ${priorityDistribution.P2}`}
                  />
                  <div
                    className={styles.stackedSegment}
                    style={{
                      width: `${(priorityDistribution.P3 / priorityTotal) * 100}%`,
                      backgroundColor: '#3b82f6',
                    }}
                    title={`P3: ${priorityDistribution.P3}`}
                  />
                  <div
                    className={styles.stackedSegment}
                    style={{
                      width: `${(priorityDistribution.P4 / priorityTotal) * 100}%`,
                      backgroundColor: '#94a3b8',
                    }}
                    title={`P4: ${priorityDistribution.P4}`}
                  />
                </div>
                <div className={styles.chartLegend}>
                  <div className={styles.legendItem}>
                    <div className={styles.legendColor} style={{ backgroundColor: '#dc2626' }} />
                    <span className={styles.legendLabel}>P1: {priorityDistribution.P1}</span>
                  </div>
                  <div className={styles.legendItem}>
                    <div className={styles.legendColor} style={{ backgroundColor: '#f59e0b' }} />
                    <span className={styles.legendLabel}>P2: {priorityDistribution.P2}</span>
                  </div>
                  <div className={styles.legendItem}>
                    <div className={styles.legendColor} style={{ backgroundColor: '#3b82f6' }} />
                    <span className={styles.legendLabel}>P3: {priorityDistribution.P3}</span>
                  </div>
                  <div className={styles.legendItem}>
                    <div className={styles.legendColor} style={{ backgroundColor: '#94a3b8' }} />
                    <span className={styles.legendLabel}>P4: {priorityDistribution.P4}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.emptyChart}>No data</div>
            )}
          </div>
        </div>
      </div>

      {/* Filters and Sort */}
      <div className={styles.controlsSection}>
        <h2 className={styles.sectionTitle}>Projects</h2>
        <div className={styles.controls}>
          <select
            value={filters.status}
            onChange={e => setFilters({ ...filters, status: e.target.value })}
            className={styles.filterSelect}
          >
            <option value="">All Statuses</option>
            {Object.keys(statusDistribution).map(status => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>

          <select
            value={filters.priority}
            onChange={e => setFilters({ ...filters, priority: e.target.value })}
            className={styles.filterSelect}
          >
            <option value="">All Priorities</option>
            <option value="P1">P1</option>
            <option value="P2">P2</option>
            <option value="P3">P3</option>
            <option value="P4">P4</option>
          </select>

          <select
            value={filters.domain}
            onChange={e => setFilters({ ...filters, domain: e.target.value })}
            className={styles.filterSelect}
          >
            <option value="">All Domains</option>
            {Object.keys(domainDistribution).map(domain => (
              <option key={domain} value={domain}>
                {domain}
              </option>
            ))}
          </select>

          <select
            value={filters.rag}
            onChange={e => setFilters({ ...filters, rag: e.target.value })}
            className={styles.filterSelect}
          >
            <option value="">All RAG Status</option>
            <option value="Green">Green</option>
            <option value="Amber">Amber</option>
            <option value="Red">Red</option>
          </select>

          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="name">Sort by Name</option>
            <option value="status">Sort by Status</option>
            <option value="priority">Sort by Priority</option>
            <option value="date">Sort by Date</option>
          </select>

          <div className={styles.resultCount}>
            {sortedProjects.length} of {totalProjects} projects
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className={styles.projectsGrid}>
        {sortedProjects.length > 0 ? (
          sortedProjects.map(project => (
            <div key={project.ProjectId} className={styles.projectCard}>
              <div className={styles.projectHeader}>
                <div className={styles.projectId}>{project.ProjectId}</div>
                <div
                  className={styles.ragBadge}
                  style={{ backgroundColor: getRagColor(project.ragStatus) }}
                  title={project.ragStatus}
                >
                  {project.ragStatus ? project.ragStatus[0].toUpperCase() : 'N'}
                </div>
              </div>

              <h3 className={styles.projectTitle}>{project.Title}</h3>

              <div className={styles.projectDescription}>
                {project.Description.substring(0, 100)}
                {project.Description.length > 100 ? '...' : ''}
              </div>

              <div className={styles.projectMeta}>
                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Status:</span>
                  <span
                    className={styles.statusBadge}
                    style={{ backgroundColor: getStatusBadgeColor(project.Status) }}
                  >
                    {project.Status}
                  </span>
                </div>

                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Priority:</span>
                  <span
                    className={styles.priorityBadge}
                    style={{ backgroundColor: getPriorityColor(project.priority) }}
                  >
                    {project.priority || 'N/A'}
                  </span>
                </div>

                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Domain:</span>
                  <span className={styles.domain}>{project.Domain}</span>
                </div>

                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Owner:</span>
                  <span className={styles.owner}>
                    {project.ProjectManagerEmail || 'Unassigned'}
                  </span>
                </div>

                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Created:</span>
                  <span className={styles.date}>
                    {new Date(project.CreatedDate).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            <p>No projects match your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}
